import { useReducer, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import { FetchPolicy } from '@apollo/client';
import DataSource from '../../../api';
import Naws from '../../../api/Naws';
import DataState from '../../../Types/DataState';
import { WeeklySummary } from '@amzn/aet-summaries-kit';
import {
  generateWeekRange,
  getLastSunday,
  mapSummariesToWeeks,
} from '../utils/weekUtils';
import {
  GetCSAWeeklySummariesResponse,
  GetCSAWeeklySummariesVariables,
} from '../../../api/Summaries/query';
import useTmAlias from './useTmAlias';
import useSummaryTargetLanguage from './useSummaryTargetLanguage';
import { useTranslationPolling } from './useTranslationPolling';

const PAGINATION_PAGE_COUNT = 4;

interface SummaryState {
  summaries: WeeklySummary[];
  isLoading: boolean;
  error: boolean;
  isPaginated: boolean;
  currentPage: number;
  totalPages: number;
  pageMetadata: Record<
    number,
    | { prevPageOldestSummary: WeeklySummary; lastEvaluatedKey: string }
    | undefined
  >;
  isRefetching: boolean;
}

type SummaryAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: GetCSAWeeklySummariesResponse }
  | { type: 'FETCH_ERROR' }
  | { type: 'ENABLE_PAGINATION' }
  | { type: 'CHANGE_PAGE'; payload: { page: number } }
  | { type: 'START_REFETCH' };

const initialState: SummaryState = {
  summaries: [],
  isLoading: false,
  error: false,
  isPaginated: false,
  currentPage: 1,
  totalPages: 1,
  pageMetadata: { 1: undefined },
  isRefetching: false,
};

const summaryReducer = (
  state: SummaryState,
  action: SummaryAction
): SummaryState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        isLoading: true,
        error: false,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        error: false,
        summaries: action.payload.summaries,
        isRefetching: false,
        // Store the key and oldest summary for the next page (if it exists)
        pageMetadata: action.payload.lastEvaluatedKey
          ? {
              ...state.pageMetadata,
              [state.currentPage + 1]: {
                lastEvaluatedKey: action.payload.lastEvaluatedKey,
                prevPageOldestSummary:
                  action.payload.summaries[action.payload.summaries.length - 1],
              },
            }
          : state.pageMetadata,
        totalPages:
          state.isPaginated &&
          action.payload.lastEvaluatedKey &&
          state.currentPage >= state.totalPages
            ? state.currentPage + 1
            : state.totalPages,
      };

    case 'FETCH_ERROR':
      return {
        ...state,
        isLoading: false,
        error: true,
        isRefetching: false,
      };

    case 'ENABLE_PAGINATION':
      return {
        ...state,
        isPaginated: true,
      };

    case 'CHANGE_PAGE':
      return {
        ...state,
        currentPage: action.payload.page,
      };

    case 'START_REFETCH':
      return {
        ...state,
        isRefetching: true,
        // Clear page keys after current page since data might have changed
        pageMetadata: Object.fromEntries(
          Object.entries(state.pageMetadata).filter(
            ([page]) => parseInt(page) <= state.currentPage
          )
        ),
      };

    default:
      return state;
  }
};

export interface UsePaginatedSummariesResult {
  summaries: WeeklySummary[];
  summariesToWeek: Array<{ week: moment.Moment; summary?: WeeklySummary }>;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  error: boolean;
  goToPage: (page: number) => void;
  refetchPage: () => void;
  lastSunday: moment.Moment;
  isPaginationMode: boolean;
  enablePagination: () => void;
  isPollingTranslation: boolean;
  hasExhaustedTranslationRetries: boolean;
}

export const usePaginatedSummaries = (
  userAlias: string,
  initialSummary?: WeeklySummary | null
): UsePaginatedSummariesResult => {
  const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');
  const [state, dispatch] = useReducer(summaryReducer, initialState);

  const lastSunday = useMemo(() => getLastSunday(), []);

  const tmAlias = useTmAlias();

  const targetLanguage = useSummaryTargetLanguage();

  const fetchSummaries = useCallback(
    (
      params: GetCSAWeeklySummariesVariables,
      fetchPolicy: FetchPolicy = 'cache-first'
    ) => {
      dispatch({ type: 'FETCH_START' });

      apiNaws.getCSAWeeklySummaries(
        { ...params, targetLanguage },
        (response: GetCSAWeeklySummariesResponse) => {
          dispatch({ type: 'FETCH_SUCCESS', payload: response });
        },
        (dataState: (typeof DataState)[keyof typeof DataState]) => {
          if (dataState === DataState.ERROR) {
            dispatch({ type: 'FETCH_ERROR' });
          }
        },
        fetchPolicy
      );
    },
    [targetLanguage]
  );

  // Helper to fetch the current page - used by translation polling and refetch
  const fetchCurrentPage = useCallback(
    (fetchPolicy: FetchPolicy = 'cache-first') => {
      const limit = state.isPaginated ? PAGINATION_PAGE_COUNT : 1;
      const startKey = state.pageMetadata[state.currentPage]?.lastEvaluatedKey;

      const queryParams: GetCSAWeeklySummariesVariables = {
        managerAlias: tmAlias,
        alias: userAlias,
        limit,
      };

      if (startKey) {
        queryParams.exclusiveStartKey = startKey;
      }

      fetchSummaries(queryParams, fetchPolicy);
    },
    [
      state.isPaginated,
      state.pageMetadata,
      state.currentPage,
      tmAlias,
      userAlias,
      fetchSummaries,
    ]
  );

  useEffect(() => {
    if (!userAlias) return;

    if (initialSummary === undefined) {
      fetchSummaries({
        managerAlias: tmAlias,
        alias: userAlias,
        limit: 1,
      });
    }
  }, [userAlias, initialSummary, fetchSummaries, tmAlias]);

  // Combine state.summaries with initialSummary for translation polling checks
  const summariesToValidateTranslations = useMemo(() => {
    if (state.summaries.length > 0) return state.summaries;
    return initialSummary ? [initialSummary] : [];
  }, [state.summaries, initialSummary]);

  const {
    isPollingTranslation,
    hasExhaustedTranslationRetries,
    hasPendingTranslation,
  } = useTranslationPolling({
    summaries: summariesToValidateTranslations,
    onPoll: useCallback(
      () => fetchCurrentPage('network-only'),
      [fetchCurrentPage]
    ),
    resetDeps: [userAlias, state.currentPage],
  });

  const summaries = useMemo(() => {
    if (state.error) return [];

    if (!state.isPaginated) {
      // Initial mode - show either initial summary or first summary from API
      if (state.summaries.length > 0) {
        return state.summaries;
      }
      return initialSummary ? [initialSummary] : [];
    }

    // Pagination mode - show all summaries
    if (state.isLoading && !state.isRefetching) {
      return [];
    }

    return state.summaries;
  }, [
    initialSummary,
    state.summaries,
    state.isPaginated,
    state.isLoading,
    state.isRefetching,
    state.error,
  ]);

  const enablePagination = useCallback(() => {
    dispatch({
      type: 'ENABLE_PAGINATION',
    });

    fetchSummaries({
      managerAlias: tmAlias,
      alias: userAlias,
      limit: PAGINATION_PAGE_COUNT,
    });
  }, [userAlias, fetchSummaries, tmAlias]);

  const goToPage = useCallback(
    (page: number) => {
      dispatch({
        type: 'CHANGE_PAGE',
        payload: { page },
      });

      const startKey = state.pageMetadata[page]?.lastEvaluatedKey;
      const queryParams: GetCSAWeeklySummariesVariables = {
        managerAlias: tmAlias,
        alias: userAlias,
        limit: PAGINATION_PAGE_COUNT,
      };

      if (startKey) {
        queryParams.exclusiveStartKey = startKey;
      }

      fetchSummaries(queryParams);
    },
    [userAlias, state.pageMetadata, fetchSummaries, tmAlias]
  );

  const refetchPage = useCallback(() => {
    dispatch({ type: 'START_REFETCH' });
    fetchCurrentPage('network-only');
  }, [fetchCurrentPage]);

  const weeks = useMemo(() => {
    if (!state.isPaginated) {
      // Initial mode - show just the most recent Sunday
      return [lastSunday];
    }

    // Pagination mode - calculate weeks based on page boundaries
    // this is to support UX to show empty states for weeks where there are no summaries
    // below code handles edge cases with our last evaluated key pagination,
    // and is effectively converting cursor pased pagination to paged based.
    if (state.summaries.length > 0) {
      const oldestSummaryOnPrevPage =
        state.pageMetadata[state.currentPage]?.prevPageOldestSummary;

      const pageStartWeek = oldestSummaryOnPrevPage
        ? moment(oldestSummaryOnPrevPage.date)
        : lastSunday
            .clone()
            .subtract((state.currentPage - 1) * PAGINATION_PAGE_COUNT, 'weeks');

      const pageEndWeek = moment.min(
        pageStartWeek.clone().subtract(PAGINATION_PAGE_COUNT - 1, 'weeks'),
        moment(state.summaries[state.summaries.length - 1].date)
      );

      return generateWeekRange(pageEndWeek, pageStartWeek);
    }

    return [];
  }, [state.isPaginated, state.summaries, state.currentPage, lastSunday]);

  // Map summaries to weeks with empty states
  const summariesToWeek = useMemo(
    () => mapSummariesToWeeks(weeks, summaries),
    [weeks, summaries]
  );

  // Keep loading state active while translation is pending and we haven't exhausted retries
  // This prevents flashing of content during translation polling
  const isLoadingWithTranslation =
    state.isLoading ||
    (hasPendingTranslation && !hasExhaustedTranslationRetries);

  return {
    summaries,
    summariesToWeek,
    currentPage: state.currentPage,
    totalPages: state.totalPages,
    isLoading: isLoadingWithTranslation,
    error: state.error,
    isPaginationMode: state.isPaginated,
    enablePagination,
    goToPage,
    refetchPage,
    lastSunday,
    isPollingTranslation,
    hasExhaustedTranslationRetries,
  };
};
